import { useState, useEffect, useCallback } from "react";
import { transformProfileCollectData, transformSequencesData, SequencesAnalyticsDaysCombinedInterface, InventoryData, PayBillData, FMSData, GenericTableData, DiscountsData, CallToActionData } from "./DataHelpers";
import { GenericTableRowInterface, ProfileCollectAnalyticsDaysCombinedInterface, ReviewInterface, ReviewGetType, MemberPermission } from "@swivl-mono/types";
import { BarAndPieChartData } from "../../Charts/BarAndPieChart/BarAndPieChart";
import { fetchNLPData, fetchUserData, fetchGenericTableRows} from "./fetchData";
import LocationsModel from "../../../Models/Locations/Locations.Model";
import { API } from "@swivl-mono/api-helper";

const SERVER_ADDRESS = "https://swivl-analytics.herokuapp.com";

// Export MenuOption from Dashboard for consistency (remove duplicate)
export { MenuOption, TitleForMenuOption } from "../BotAnalyticsDashboard";

// Review data interface
interface ReviewData {
  byDateTime: {
    [key: string]: {
      total: number;
      positiveReviews: number;
      negativeReviews: number;
      dayOfWeek: string;
      byTimeOfDay: string;
      locationId: number;
      locationName: string;
      reviews: ReviewInterface[];
    };
  };
  updatedByDateTime: {
    [key: string]: {
      total: number;
      positiveReviews: number;
      negativeReviews: number;
      dayOfWeek: string;
      byTimeOfDay: string;
      locationId: number;
      locationName: string;
      reviews: ReviewInterface[];
    };
  };
}

// Review API functions
async function fetchReviews(orgId: number, type: ReviewGetType): Promise<ReviewInterface[]> {
  try {
    const reviews = await API.Reviews.getReviews(orgId, type);
    return reviews;
  } catch (error) {
    console.error("Error fetching reviews:", error);
    return [];
  }
}

async function fetchLocationNames(orgId: number): Promise<{ [key: number]: string }> {
  try {
    const response = await API.Locations.getGroupsAndLocations(orgId);
    const locations = response.flatMap((group) => group.locations);
    
    // Count how many locations share the same name
    const nameCount: { [key: string]: number } = {};
    locations.forEach((location) => {
      nameCount[location.name] = (nameCount[location.name] || 0) + 1;
    });
    
    // Create location map with appropriate naming
    const locationMap = locations.reduce(
      (acc, location) => {
        // Only include location ID if there are multiple locations with the same name
        if (nameCount[location.name] > 1) {
          acc[location.id] = `${location.name} (${location.id})`;
        } else {
          acc[location.id] = location.name;
        }
        return acc;
      },
      {} as { [key: number]: string },
    );
    return locationMap;
  } catch (error) {
    console.error("Error fetching location names:", error);
    return {};
  }
}

// Helper function to extract locationIds from MemberPermission
function extractLocationIds(locationPermission: MemberPermission, groups: any): number[] {
  if (!locationPermission.locations) return [];
  
  const { locations } = locationPermission;
  
  // If orgWide, return empty array (no filtering)
  if (locations.orgId) return [];
  
  // Extract locationIds from groups and individual locations
  let locationIds: number[] = [];
  
  // Add locations from selected groups
  if (locations.groupIds) {
    locations.groupIds.forEach(groupId => {
      // Find group in array by id
      const group = Array.isArray(groups) 
        ? groups.find(g => g.id === groupId)
        : groups[groupId];
      if (group) {
        locationIds.push(...group.locations.map((loc: any) => loc.id));
      }
    });
  }
  
  // Add individual locations
  if (locations.locationIds) {
    locationIds.push(...locations.locationIds);
  }
  
  return [...new Set(locationIds)]; // Remove duplicates
}

// Review data transformation function
function transformReviewData(
  reviews: ReviewInterface[], 
  startDay: string, 
  endDay: string, 
  locationNames: { [key: number]: string },
  locationPermission?: MemberPermission,
  groups?: any
): ReviewData {
  const byDateTime = {};
  const updatedByDateTime = {};

  // Filter reviews by locationIds if provided
  const locationIds = locationPermission ? extractLocationIds(locationPermission, groups) : [];
  const filteredReviews = locationIds.length 
    ? reviews.filter(review => locationIds.includes(review.locationId))
    : reviews;

  filteredReviews.forEach((review) => {
    const reviewDate = new Date(review.reviewDate);
    if (reviewDate >= new Date(startDay) && reviewDate <= new Date(endDay)) {
      const day = reviewDate.toISOString().split("T")[0];
      const dayOfWeek = reviewDate.toLocaleString("en-US", { weekday: "long" });
      const hour = reviewDate.getHours();
      let minutes = reviewDate.getMinutes();
      if (minutes < 15) {
        minutes = 0;
      } else if (minutes < 45) {
        minutes = 30;
      } else {
        minutes = 0;
        reviewDate.setHours(hour + 1);
      }
      const byTimeOfDay = `${hour.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
      const locationName = locationNames[review.locationId] || "Unknown";

      if (!byDateTime[day]) {
        byDateTime[day] = {
          total: 0,
          positiveReviews: 0,
          negativeReviews: 0,
          dayOfWeek: dayOfWeek,
          byTimeOfDay: byTimeOfDay,
          locationId: review.locationId,
          locationName: locationName,
          reviews: [],
        };
      }
      byDateTime[day].total += 1;
      byDateTime[day].reviews.push(review);
      if (review.rating >= 4) {
        byDateTime[day].positiveReviews += 1;
      } else if (review.rating <= 3) {
        byDateTime[day].negativeReviews += 1;
      }
    }

    review.history.forEach((history) => {
      const historyDate = new Date(history.date);
      if (historyDate >= new Date(startDay) && historyDate <= new Date(endDay)) {
        const day = historyDate.toISOString().split("T")[0];
        const dayOfWeek = historyDate.toLocaleString("en-US", { weekday: "long" });
        const hour = historyDate.getHours();
        let minutes = historyDate.getMinutes();
        if (minutes < 15) {
          minutes = 0;
        } else if (minutes < 45) {
          minutes = 30;
        } else {
          minutes = 0;
          historyDate.setHours(hour + 1);
        }
        const byTimeOfDay = `${hour.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
        const locationName = locationNames[review.locationId] || "Unknown";

        if (!updatedByDateTime[day]) {
          updatedByDateTime[day] = {
            total: 0,
            positiveReviews: 0,
            negativeReviews: 0,
            dayOfWeek: dayOfWeek,
            byTimeOfDay: byTimeOfDay,
            locationId: review.locationId,
            locationName: locationName,
            reviews: [],
          };
        }
        updatedByDateTime[day].total += 1;
        updatedByDateTime[day].reviews.push(review);
        if (review.rating >= 4) {
          updatedByDateTime[day].positiveReviews += 1;
        } else if (review.rating <= 3) {
          updatedByDateTime[day].negativeReviews += 1;
        }
      }
    });
  });
  return { byDateTime, updatedByDateTime };
}

// Note: Rating data transformation moved to ReviewDataHelpers.ts

export const useBotAnalyticsData = (
  orgId: number, 
  startDay: string, 
  endDay: string, 
  menuOptions = {},
  locationPermission?: MemberPermission
) => {
    const { groups } = LocationsModel.useState();

    const [data, setData] = useState<{
        nlpData: any;
        userData: any;
        cortexData: BarAndPieChartData[];
        inventoryData: BarAndPieChartData[];
        payBillData: BarAndPieChartData[];
        fmsLeadData: BarAndPieChartData[];
        rawProfileCollectData: GenericTableRowInterface[];
        profileCollectData: ProfileCollectAnalyticsDaysCombinedInterface;
        rawSequencesData: GenericTableRowInterface[];
        sequencesData: SequencesAnalyticsDaysCombinedInterface;
        reviewData: ReviewData | null;
        locationNames: { [key: number]: string };
        discountsData: BarAndPieChartData[];
        callToActionData: BarAndPieChartData[];
    }>({
        nlpData: null,
        userData: null,
        cortexData: [],
        inventoryData: [],
        payBillData: [],
        fmsLeadData: [],
        rawProfileCollectData: [],
        profileCollectData: {} as ProfileCollectAnalyticsDaysCombinedInterface,
        rawSequencesData: [],
        sequencesData: { total: 0, byDayOfYear: {}, byTimeOfDay: {}, byDayOfWeek: {}, bySequenceType: {} },
        reviewData: null,
        locationNames: {},
        discountsData: [],
        callToActionData: [],
    });

    useEffect(() => {
        const loadData = async () => {
            try {
                const [
                    nlp, 
                    user, 
                    cortex00, 
                    cortex01, 
                    inventory, 
                    payBill, 
                    fms, 
                    profileCollect, 
                    discounts, 
                    callToAction,
                    sequencesPastDue,
                    sequencesMoveOut,
                    sequencesMoveIn,
                    sequencesAbandonedCarts
                ] = await Promise.all([
                    fetchNLPData(orgId, startDay, endDay),
                    fetchUserData(orgId, startDay, endDay),
                    fetchGenericTableRows("my-storage-00", startDay, endDay, locationPermission, groups),
                    fetchGenericTableRows("my-storage-01", startDay, endDay, locationPermission, groups),
                    fetchGenericTableRows("shoppingfunnel-00", startDay, endDay, locationPermission, groups),
                    fetchGenericTableRows("paybill-00", startDay, endDay, locationPermission, groups),
                    fetchGenericTableRows("fms-00", startDay, endDay, locationPermission, groups),
                    fetchGenericTableRows("profile-collect-00", startDay, endDay, locationPermission, groups),
                    fetchGenericTableRows("discounts-00", startDay, endDay, locationPermission, groups),
                    fetchGenericTableRows("cta-00", startDay, endDay, locationPermission, groups),
                    fetchGenericTableRows("fms-plus-past-due-00", startDay, endDay, locationPermission, groups),
                    fetchGenericTableRows("fms-plus-move-out-00", startDay, endDay, locationPermission, groups),
                    fetchGenericTableRows("fms-plus-move-in-00", startDay, endDay, locationPermission, groups),
                    fetchGenericTableRows("fms-plus-abandoned-carts-00", startDay, endDay, locationPermission, groups),
                ]);

                const cortexData00 = GenericTableData(cortex00 || []);
                const cortexData01 = GenericTableData(cortex01 || []);
                const combinedCortexData = [...cortexData00, ...cortexData01];

                // Combine all sequences data
                const allSequences = [
                    ...(sequencesPastDue || []),
                    ...(sequencesMoveOut || []),
                    ...(sequencesMoveIn || []),
                    ...(sequencesAbandonedCarts || [])
                ];
                const sequencesData = transformSequencesData(allSequences);

                setData({
                    nlpData: nlp || [],
                    userData: user || [],
                    cortexData: combinedCortexData || [],
                    inventoryData: inventory ? InventoryData(inventory) : [],
                    payBillData: payBill ? PayBillData(payBill) : [],
                    fmsLeadData: fms ? FMSData(fms) : [],
                    rawProfileCollectData: profileCollect || [],
                    profileCollectData: profileCollect ? transformProfileCollectData(profileCollect) : {} as ProfileCollectAnalyticsDaysCombinedInterface,
                    rawSequencesData: allSequences || [],
                    sequencesData: sequencesData,
                    reviewData: null, // Will be loaded lazily
                    locationNames: {},
                    discountsData: discounts ? DiscountsData(discounts) : [],
                    callToActionData: callToAction ? CallToActionData(callToAction) : [],
                });

            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        loadData();
    }, [orgId, startDay, endDay, locationPermission, groups]);

    // Lazy loading function for review data
    const loadReviewData = useCallback(async (locationPermissionOverride?: MemberPermission) => {
        try {
            const [reviews, locationNames] = await Promise.all([
                fetchReviews(orgId, ReviewGetType.ALL),
                fetchLocationNames(orgId)
            ]);
            
            const currentLocationPermission = locationPermissionOverride || locationPermission;
            const reviewData = transformReviewData(reviews, startDay, endDay, locationNames, currentLocationPermission, groups);
            
            setData(prevData => ({
                ...prevData,
                reviewData,
                locationNames
            }));
        } catch (error) {
            console.error("Error loading review data:", error);
        }
    }, [orgId, startDay, endDay, locationPermission, groups]);

    return { 
        ...data, 
        loadReviewData
    };
};